'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { warnOnce } from '@mui/x-internals/warning';
import { ChatMessageGroup } from "../ChatMessage/ChatMessageGroup.mjs";
import { ChatDateDivider } from "../ChatMessage/ChatDateDivider.mjs";
import { ChatUnreadMarker } from "../ChatIndicators/ChatUnreadMarker.mjs";
import { ChatStreamingIndicatorRow } from "../ChatIndicators/ChatStreamingIndicatorRow.mjs";
import { useChatSlots } from "../internals/ChatSlotsContext.mjs";
import { resolveSlotProps } from "../internals/mergeSlotProps.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function warnIfHostElementRowSlot(slotName, value) {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  if (typeof value === 'string') {
    warnOnce([`MUI X: The \`${slotName}\` slot was given a host element (e.g. \`'div'\`).`, `The ${slotName} is a self-suppressing row component: it reads \`messageId\`/\`index\`/\`items\` and renders only at its boundary, returning \`null\` otherwise.`, `A host element ignores those props (leaking them onto the DOM node) and renders on every message row.`, `Pass a component, or \`null\` to hide the slot entirely.`]);
  }
}
function warnIfDividerSlotWithoutFeature(slotName, hasCustomization) {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  if (hasCustomization) {
    warnOnce([`MUI X: A \`${slotName}\` slot or slotProps entry was provided, but the \`${slotName}\` feature is disabled.`, `Dividers are opt-in and render nothing unless enabled, so the customization has no effect.`, `Pass \`features={{ ${slotName}: true }}\` to render it.`]);
  }
}

/**
 * Opt-in row dividers rendered by the default message row. Shared by `ChatBox`
 * (`features` prop) and the standalone `ChatMessageList` (`features` prop).
 */

/**
 * Flat per-row slot keys shared by `ChatBox` and `ChatMessageList` — the
 * message-rendering pipeline vocabulary (group wrapper, dividers, and the
 * per-row `message*` parts). A subset of the public `ChatBoxSlots`.
 */

/**
 * Default message-row composition used by `ChatBox` and `ChatMessageList`
 * when no custom `renderItem` is provided.
 *
 * Slots are read from the `ChatSlots` context (the `ChatBox` path) unless passed
 * explicitly via props (the standalone `ChatMessageList` path, which partitions
 * its own flat slots and forwards the row keys here). `messageGroup` swaps the
 * group component; the flat `message*` keys flow into the group, which maps them
 * onto the inner `ChatMessage`'s short local slots.
 *
 * Memoized because this is the per-row component inside the virtualized list:
 * `ChatBox`'s `renderItem` carries no slot payload (it reads context), so
 * scroll-driven re-renders short-circuit on the shallow id/index/items compare.
 */
export const DefaultMessageItem = /*#__PURE__*/React.memo(function DefaultMessageItem({
  id,
  index,
  items,
  slots: slotsProp,
  slotProps: slotPropsProp,
  features
}) {
  const context = useChatSlots();
  const slots = slotsProp ?? context.slots;
  const slotProps = slotPropsProp ?? context.slotProps;

  // Row-level dividers are opt-in capabilities: absent unless the corresponding
  // feature flag is enabled. Once enabled, both the date divider and the unread
  // marker are self-suppressing: they read `messageId`/`index`/`items` and render
  // only at their boundary (a new calendar day, or the first unread message derived
  // from the active conversation's `unreadCount`/`readState`), returning null
  // otherwise. The slot then customizes the rendered component (`undefined` falls
  // back to the default; `null` still hides it). A custom slot must be a component
  // (it receives `messageId`/`index`/`items`); a raw host element would both fail
  // to self-suppress and leak those as DOM attributes.
  const showDateDivider = features?.dateDivider === true && slots.dateDivider !== null;
  const showUnreadMarker = features?.unreadMarker === true && slots.unreadMarker !== null;
  const DateDividerComponent = slots.dateDivider ?? ChatDateDivider;
  const UnreadMarkerComponent = slots.unreadMarker ?? ChatUnreadMarker;

  // Streaming indicator: enabled by default ('auto'), unlike the opt-in dividers.
  // The trailing row below covers the waiting phase (response in flight, no
  // assistant message yet); the in-bubble phase is rendered by `ChatMessage`.
  // Disabling the feature nulls the slot for the whole row pipeline, so the
  // in-bubble instance is gated without threading the mode through slotProps.
  const streamingIndicatorMode = features?.streamingIndicator ?? 'auto';
  const showStreamingIndicator = streamingIndicatorMode !== false && slots.streamingIndicator !== null;
  const isLastRow = items != null && index != null && index === items.length - 1;
  const rowSlots = React.useMemo(() => streamingIndicatorMode === false ? _extends({}, slots, {
    streamingIndicator: null
  }) : slots, [slots, streamingIndicatorMode]);
  warnIfHostElementRowSlot('dateDivider', slots.dateDivider);
  warnIfHostElementRowSlot('unreadMarker', slots.unreadMarker);
  warnIfHostElementRowSlot('streamingIndicator', slots.streamingIndicator);
  warnIfDividerSlotWithoutFeature('dateDivider', features?.dateDivider !== true && (slots.dateDivider != null || slotProps.dateDivider != null));
  warnIfDividerSlotWithoutFeature('unreadMarker', features?.unreadMarker !== true && (slots.unreadMarker != null || slotProps.unreadMarker != null));
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [showDateDivider && /*#__PURE__*/_jsx(DateDividerComponent, _extends({}, resolveSlotProps(slotProps.dateDivider ?? {}, {
      messageId: id,
      index,
      items
    }), {
      messageId: id,
      index: index,
      items: items
    })), showUnreadMarker && /*#__PURE__*/_jsx(UnreadMarkerComponent, _extends({}, resolveSlotProps(slotProps.unreadMarker ?? {}, {
      messageId: id,
      index,
      items
    }), {
      messageId: id,
      index: index,
      items: items
    })), /*#__PURE__*/_jsx(ChatMessageGroup, {
      messageId: id,
      index: index,
      items: items,
      slots: rowSlots,
      slotProps: slotProps
    }), showStreamingIndicator && isLastRow && (slots.streamingIndicator ? (/*#__PURE__*/React.createElement(slots.streamingIndicator, _extends({
      mode: streamingIndicatorMode,
      message: null,
      messageId: id,
      index,
      items
    }, resolveSlotProps(slotProps.streamingIndicator ?? {}, {
      messageId: id,
      index,
      items
    })))) : /*#__PURE__*/_jsx(ChatStreamingIndicatorRow, {
      mode: streamingIndicatorMode,
      messageId: id,
      index: index,
      items: items,
      hasAvatar: slots.messageAvatar !== null,
      slotProps: slotProps.streamingIndicator
    }))]
  });
});
if (process.env.NODE_ENV !== "production") DefaultMessageItem.displayName = "DefaultMessageItem";